import React from 'react';
import { CorporateFare as CorporateFareIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { isDevLoginMode } from '../../config/env';
import { api, isUploadImageForAccessEnabled } from '../services/api';

const DEFAULT_MESSAGE = 'Unauthorized Access!';
const NO_ACCESS_MESSAGE = 'You do not currently have access to this application. For assistance, please contact the Transformation Office.';
const TOKEN_EXPIRED_MESSAGE = 'You are logged out. Please close this window and re login from HIP or Super App';
const DEFAULT_APP = 'super-picker';

const NO_ACCESS_UPLOAD_IMAGE_URL =
  'https://firebasestorage.googleapis.com/v0/b/super-picker-41006.firebasestorage.app/o/edm%2FSuper%20Picker%20EDM3.jpeg?alt=media&token=affbfbc8-9e5e-4caa-a5c9-61e9ae4d77e2';
const NO_ACCESS_CALCULATOR_UPLOAD_IMAGE_URL =
  'https://firebasestorage.googleapis.com/v0/b/super-picker-41006.firebasestorage.app/o/edm%2Fsuper_calculator_3.png?alt=media&token=b279a0ac-8357-4207-a3f8-7ef740981559';
const LINUS_WHATSAPP_URL = 'http://wa.me/6582253878';
const MAX_UPLOAD_DIMENSION = 1600;
const NO_ACCESS_UPLOAD_TOKEN_KEY = 'sp_no_access_upload_token';

function getUnauthorizedParams(): { reason: string | null; ssoToken: string | null; app: string } {
  const params = new URLSearchParams(window.location.search);
  let fallbackToken: string | null = null;
  try {
    fallbackToken = sessionStorage.getItem(NO_ACCESS_UPLOAD_TOKEN_KEY);
  } catch {
    fallbackToken = null;
  }
  return {
    reason: params.get('reason'),
    ssoToken: params.get('ssoToken') || fallbackToken,
    app: (params.get('app') || DEFAULT_APP).toLowerCase(),
  };
}

function getUnauthorizedMessage(
  reason: string | null,
  uploadEnabled: boolean,
  app: string,
  devLoginMode: boolean,
): string | React.ReactNode {
  const appName =
    app === 'super-calculator' ? 'Super Calculator' :
    app === 'super-connector' ? 'Super Connector' :
    app === 'communication' ? 'Communication' :
    app === 'project-essential' ? 'Project Essentials' :
    'Super Picker';
  const noAccessMessage = NO_ACCESS_MESSAGE.replace('this application', appName);
  if (reason === 'no_access') {
    if (devLoginMode) {
      return noAccessMessage;
    }
    if (uploadEnabled && app === 'super-calculator') {
      return (
        <>
          Welcome to Super Calculator. To get immediate access, simply
          <br />
          <br />
          1) Complete any of the actions below:
          <br />
          &nbsp;&nbsp;&nbsp;a) share this{' '}
          <a
            href={NO_ACCESS_CALCULATOR_UPLOAD_IMAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#fbbf24', textDecoration: 'underline' }}
          >
            image
          </a>{' '}on any of your social media platform
          <br />
          &nbsp;&nbsp;&nbsp;b) follow, like and share this{' '}
          <a
            href="https://vt.tiktok.com/ZSH2W6TX8/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#fbbf24', textDecoration: 'underline' }}
          >
            post
          </a>
          <br />
          &nbsp;&nbsp;&nbsp;c) share{' '}
          <a
            href={`https://api.whatsapp.com/send?text=${encodeURIComponent('Early Access to Super Calculator ' + window.location.origin + '/share/super-calculator.html')}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#fbbf24', textDecoration: 'underline' }}
          >
            this
          </a>{' '}
          via WhatsApp
          <br />
          <br />
          2) upload a screenshot of the shared post.
          <br />
          <br />
          If you need help, please contact Linus at{' '}
          <a href={LINUS_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24', textDecoration: 'underline' }}>
            8225 3878
          </a>
          .
        </>
      );
    }
    if (uploadEnabled && app === 'super-picker') {
      return (
        <>
          Welcome to Super Picker. To get immediate access, simply
          <br />
          <br />
          1) Complete any of the actions below:
          <br />
          &nbsp;&nbsp;&nbsp;a) share this{' '}
          <a href={NO_ACCESS_UPLOAD_IMAGE_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24', textDecoration: 'underline' }}>
            image
          </a>{' '}
          on any of your social media platform
          <br />
          &nbsp;&nbsp;&nbsp;b) follow, like and share this{' '}
          <a
            href="https://vt.tiktok.com/ZSuRGvtN1/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#fbbf24', textDecoration: 'underline' }}
          >
            post
          </a>
          <br />
          &nbsp;&nbsp;&nbsp;c) share{' '}
          <a
            href={`https://api.whatsapp.com/send?text=${encodeURIComponent('Early Access to Super Picker ' + window.location.origin + '/share/super-picker.html')}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#fbbf24', textDecoration: 'underline' }}
          >
            this
          </a>{' '}
          via WhatsApp
          <br />
          <br />
          2) upload a screenshot of the shared post.
          <br />
          <br />
          If you need help, please contact Linus at{' '}
          <a href={LINUS_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24', textDecoration: 'underline' }}>
            8225 3878
          </a>
          .
        </>
      );
    }
    return noAccessMessage;
  }
  if (reason === 'token_expired') {
    return TOKEN_EXPIRED_MESSAGE;
  }
  return DEFAULT_MESSAGE;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read image'));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to process image'));
    img.src = dataUrl;
  });
}

async function resizeImageForUpload(file: File): Promise<string> {
  const inputDataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(inputDataUrl);
  const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(image.width, image.height));
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to initialize image processor');
  }
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL('image/jpeg', 0.82);
}

export default function UnauthorizedEntryPage() {
  const { reason, ssoToken, app } = getUnauthorizedParams();
  const uploadEnabled = isUploadImageForAccessEnabled() && !isDevLoginMode;
  const isNoAccess = reason === 'no_access';
  const uploadApp: 'super-picker' | 'super-calculator' | null =
    app === 'super-picker' || app === 'super-calculator' ? app : null;
  const canUpload = Boolean(uploadEnabled && isNoAccess && uploadApp);
  const message = getUnauthorizedMessage(reason, uploadEnabled, app, isDevLoginMode);
  const appName =
    app === 'super-calculator' ? 'Super Calculator' :
    app === 'super-connector' ? 'Super Connector' :
    app === 'communication' ? 'Communication' :
    app === 'project-essential' ? 'Project Essentials' :
    'Super Picker';
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState<boolean>(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState<boolean>(false);

  const handleUploadConfirm = async () => {
    if (!selectedFile) {
      setUploadError('Please select an image to upload.');
      return;
    }
    if (!ssoToken) {
      setUploadError('Missing login token. Please re-login from HIP or Super App.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const resizedDataUrl = await resizeImageForUpload(selectedFile);
      const targetApp = uploadApp ?? 'super-picker';
      await api.requestAccessUpload(ssoToken, resizedDataUrl, targetApp);
      try {
        sessionStorage.removeItem(NO_ACCESS_UPLOAD_TOKEN_KEY);
      } catch {
        // Ignore storage cleanup failure.
      }
      window.location.replace(targetApp === 'super-calculator' ? '/super-calculator' : '/');
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        background:
          'radial-gradient(1200px 600px at 12% 0%, rgba(251, 191, 36, 0.22), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(56, 189, 248, 0.2), transparent 55%), linear-gradient(180deg, #0b1220 0%, #1f2937 100%)',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 760 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
          <CorporateFareIcon sx={{ fontSize: { xs: 38, md: 48 }, color: '#fbbf24' }} />
          <Typography
            sx={{
              color: '#f8fafc',
              fontWeight: 900,
              letterSpacing: { xs: 1.5, md: 3 },
              fontSize: { xs: '1.9rem', md: '3rem' },
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            TRANSFORMATION OFFICE
          </Typography>
        </Stack>

        <Paper
          elevation={8}
          sx={{
            maxWidth: 640,
            width: '100%',
            p: { xs: 3, md: 4 },
            mx: 'auto',
            background: 'linear-gradient(130deg, #0f172a 0%, #172554 100%)',
            color: '#e2e8f0',
            border: '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
        <Typography
          variant="overline"
          sx={{ display: 'block', fontWeight: 700, letterSpacing: 1.8, color: '#f59e0b', mb: 0.5 }}
        >
          {appName.toUpperCase()}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#f8fafc' }}>
          {`Welcome to ${appName}`}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: '#cbd5e1', whiteSpace: 'pre-line' }}>
          {message}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {canUpload && (
            <Button variant="contained" onClick={() => setUploadDialogOpen(true)}>
              Upload Screenshot
            </Button>
          )}
        </Stack>
        {canUpload && !ssoToken && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Missing login token in URL. Please retry login from HIP or Super App.
          </Alert>
        )}
        </Paper>
      </Box>

      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Shared Post Screenshot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 0.5 }}>
            Upload a screenshot of your social media shared post to request access.
          </Typography>
          <Button variant="outlined" component="label" disabled={uploading}>
            Choose Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                setUploadError(null);
                const file = event.target.files?.[0] || null;
                setSelectedFile(file);
              }}
            />
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
            {selectedFile ? `Selected: ${selectedFile.name}` : 'No file selected'}
          </Typography>
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUploadConfirm} disabled={uploading || !selectedFile}>
            {uploading ? <CircularProgress size={18} color="inherit" /> : 'Upload & Login'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
